import moment from "moment";
import axiosInstance from "../api/axiosInstance";
import { toast } from "react-toastify";
import { useState, useEffect } from 'react';
import DOMPurify from "dompurify";

export const handleQuickFill = (setFormData, formData) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.xml';
    input.onchange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const requestFormData = new FormData();
        requestFormData.append('xml', file);
        axiosInstance.post('/utils/parseCprReaderResult', requestFormData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        })
            .then((res) => {
                const result = res.data;
                if (result.success === false) {
                    console.error('Error from server:', result.message);
                    return;
                }
                result.base64Image = result.photo;
                delete result.photo;
                result.civilIdNumber = result.civilNumber;
                result.dateOfBirth = moment(result.dateOfBirth).format("YYYY-MM-DD");
                let newData = JSON.parse(JSON.stringify(formData));
                newData = Object.assign(newData, result);
                setFormData(newData);
                toast("File processed successfully", { type: "success" });

            })
            .catch((error) => {
                toast("Error while processing file", { type: "error" });
                console.error('Error:', error); // Handle any network/parsing errors
            });

    };

    input.click();
};

// SmartCard reading must happen locally — the USB reader is on the user's PC,
// not the remote server. The local bridge (smartcard-bridge.js) runs on the
// user's Windows machine and serves on http://localhost:7427.
const SMARTCARD_BRIDGE_URL = 'http://localhost:7427/readCard';

export const handleSmartCardRead = async (setFormData, formData, setLoading) => {
    setLoading(true);
    try {
        const res = await fetch(SMARTCARD_BRIDGE_URL, { targetAddressSpace: 'local' });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            toast(err.message || 'Card read failed', { type: 'error' });
            return;
        }
        const result = await res.json();
        if (result.success === false) {
            toast(result.message || 'Card read failed', { type: 'error' });
            return;
        }
        // Resolve country name → nationality string via backend
        if (result.englishCountryName) {
            try {
                const natRes = await axiosInstance.get(`/utils/resolveNationality?country=${encodeURIComponent(result.englishCountryName)}`);
                result.nationality = natRes.data?.nationality || '';
            } catch { /* leave nationality empty */ }
        }
        result.base64Image = result.photo;
        delete result.photo;
        result.civilIdNumber = result.civilNumber;
        if (result.dateOfBirth) {
            result.dateOfBirth = moment(result.dateOfBirth).format('YYYY-MM-DD');
        }
        let newData = JSON.parse(JSON.stringify(formData));
        newData = Object.assign(newData, result);
        setFormData(newData);
        toast('Card read successfully', { type: 'success' });
    } catch (err) {
        if (err.name === 'TypeError' && err.message.includes('fetch')) {
            toast('SmartCard bridge is not running. Please run install.bat on this PC.', { type: 'error' });
        } else {
            toast('Error reading card', { type: 'error' });
        }
        console.error('SmartCard read error:', err);
    } finally {
        setLoading(false);
    }
};

export function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => clearTimeout(handler);
    }, [value, delay]);

    return debouncedValue;
}

export function nl2br(data, sanitize = true) {
    if (!data) {
        return "";
    }
    if (sanitize) {
        return DOMPurify.sanitize(data.replaceAll("\n", "<br />"))
    }
    return data.replaceAll("\n", "<br />")
}

export const teethFdiMap = {
    "1": "18", "2": "17", "3": "16", "4": "15", "5": "14", "6": "13", "7": "12", "8": "11",
    "9": "21", "10": "22", "11": "23", "12": "24", "13": "25", "14": "26", "15": "27", "16": "28",
    "17": "38", "18": "37", "19": "36", "20": "35", "21": "34", "22": "33", "23": "32", "24": "31",
    "25": "41", "26": "42", "27": "43", "28": "44", "29": "45", "30": "46", "31": "47", "32": "48"
}

/**
 * Primary (deciduous) teeth are stored by their FDI/ISO primary id and relabelled
 * to the Universal letters A-T for display, mirroring how permanent teeth are
 * stored as Universal 1-32 and relabelled to FDI. Keeping the stored id numeric
 * matters: the dental chart's hit-test derives the id by stripping every
 * non-digit from the clicked element's class name.
 */
export const primaryUniversalMap = {
    "55": "A", "54": "B", "53": "C", "52": "D", "51": "E",
    "61": "F", "62": "G", "63": "H", "64": "I", "65": "J",
    "75": "K", "74": "L", "73": "M", "72": "N", "71": "O",
    "81": "P", "82": "Q", "83": "R", "84": "S", "85": "T"
}

export const PRIMARY_TOOTH_IDS = Object.keys(primaryUniversalMap)

export const isPrimaryToothId = (id) => Object.prototype.hasOwnProperty.call(primaryUniversalMap, id + "")

/**
 * The single place a tooth id becomes something a clinician reads. Primary and
 * permanent ids never overlap, so the id alone says which dentition it belongs to.
 */
export function toothLabel(id, isFdi = true) {
    const key = id + ""
    if (isPrimaryToothId(key)) {
        return isFdi ? key : primaryUniversalMap[key]
    }
    return isFdi ? teethFdiMap[key] : key
}

export function getTeethNos(serviceDetails, isFdi = true) {
    const isArray = !serviceDetails ? [] : Array.isArray(serviceDetails)

    if (!serviceDetails || serviceDetails.length === 0) {
        return []
    }
    try {
        const arr = isArray ? serviceDetails : JSON.parse(serviceDetails);
        return [...new Set(arr.map(i => toothLabel(i.id, isFdi)))]
    } catch (e) {
        console.error(e)
    }
    return []
}

export const teethDescription = (type = "Standard") => {
    let desc = {
        "1": "Upper Left Third Molar (Wisdom Tooth)",
        "2": "Upper Left Second Molar",
        "3": "Upper Left First Molar",
        "4": "Upper Left Second Premolar",
        "5": "Upper Left First Premolar",
        "6": "Upper Left Canine",
        "7": "Upper Left Lateral Incisor",
        "8": "Upper Left Central Incisor",
        "9": "Upper Right Central Incisor",
        "10": "Upper Right Lateral Incisor",
        "11": "Upper Right Canine",
        "12": "Upper Right First Premolar",
        "13": "Upper Right Second Premolar",
        "14": "Upper Right First Molar",
        "15": "Upper Right Second Molar",
        "16": "Upper Right Third Molar (Wisdom Tooth)",
        "17": "Lower Right Third Molar (Wisdom Tooth)",
        "18": "Lower Right Second Molar",
        "19": "Lower Right First Molar",
        "20": "Lower Right Second Premolar",
        "21": "Lower Right First Premolar",
        "22": "Lower Right Canine",
        "23": "Lower Right Lateral Incisor",
        "24": "Lower Right Central Incisor",
        "25": "Lower Left Central Incisor",
        "26": "Lower Left Lateral Incisor",
        "27": "Lower Left Canine",
        "28": "Lower Left First Premolar",
        "29": "Lower Left Second Premolar",
        "30": "Lower Left First Molar",
        "31": "Lower Left Second Molar",
        "32": "Lower Left Third Molar (Wisdom Tooth)",

        // Primary (deciduous) dentition. The ids are disjoint from the permanent
        // 1-32 above, so one merged table serves both charts.
        "55": "Upper Left Primary Second Molar",
        "54": "Upper Left Primary First Molar",
        "53": "Upper Left Primary Canine",
        "52": "Upper Left Primary Lateral Incisor",
        "51": "Upper Left Primary Central Incisor",
        "61": "Upper Right Primary Central Incisor",
        "62": "Upper Right Primary Lateral Incisor",
        "63": "Upper Right Primary Canine",
        "64": "Upper Right Primary First Molar",
        "65": "Upper Right Primary Second Molar",
        "75": "Lower Right Primary Second Molar",
        "74": "Lower Right Primary First Molar",
        "73": "Lower Right Primary Canine",
        "72": "Lower Right Primary Lateral Incisor",
        "71": "Lower Right Primary Central Incisor",
        "81": "Lower Left Primary Central Incisor",
        "82": "Lower Left Primary Lateral Incisor",
        "83": "Lower Left Primary Canine",
        "84": "Lower Left Primary First Molar",
        "85": "Lower Left Primary Second Molar"
    }
    return desc
};

export const dentalMapping = {
    "surface": "Surface",
    "root": "Root",
    "side": "Side",
    "tooth": "Whole Tooth",
    "extraction": "Extraction",
    "crown": "Crown",
    "implant": "Implant",
    "whole_mouth": "Whole Mouth"
};

export const topViewDescription = {
    o: "(Occlusal) Chewing surface",
    i: "(Incisal) Cutting edge",
    m: "(Mesial) Side facing the middle of mouth",
    d: "(Distal) Side away from middle",
    f: "(Buccal / Facial) Outer surface",
    l: "(Lingual / Palatal) Inner surface"
}


export const getCurrentUserId = () => {
    try {
        const userMedical = localStorage.getItem("userMedical");
        if (userMedical) {
            const userData = JSON.parse(userMedical);
            return userData?.id || userData?.data?.id || null;
        }
        return null;
    } catch (error) {
        console.error("Error parsing userMedical from localStorage:", error);
        return null;
    }
};

export const getActiveBranchId = () => {
    const id = localStorage.getItem('activeBranchId');
    return id ? parseInt(id) : null;
};

export const getActiveBranchSettings = () => {
    try {
        return JSON.parse(localStorage.getItem('activeBranchSettings') || 'null');
    } catch {
        return null;
    }
};

// Checks whether the selected service requires consent(s) that have not yet
// been recorded for the patient today. Fails open (returns ok: true) on any
// unexpected error so infra hiccups never block adding a service.
export const checkForceConsents = async (serviceId, patientId) => {
    try {
        const settingsRes = await axiosInstance.get('getModuleSettings');
        const forceConsentsRow = settingsRes.data?.data?.find(r => r.module_name === 'force_consents');
        if (!forceConsentsRow || !forceConsentsRow.enable) {
            return { ok: true };
        }

        const serviceConsentsRes = await axiosInstance.get(`getServiceConsents/${serviceId}`);
        const requiredIds = (serviceConsentsRes.data?.data || []).map(r => r.consent_id);
        if (requiredIds.length === 0) {
            return { ok: true };
        }

        const patientFormsRes = await axiosInstance.get(`getPatientConsentForms/${patientId}`);
        const forms = patientFormsRes.data?.data || [];
        const today = moment().format('YYYY-MM-DD');
        const signedTodayIds = new Set(
            forms
                .filter(f => moment(f.created_at).format('YYYY-MM-DD') === today)
                .map(f => f.template_id)
        );

        const missingIds = requiredIds.filter(id => !signedTodayIds.has(id));
        if (missingIds.length === 0) {
            return { ok: true };
        }

        const templatesRes = await axiosInstance.get('consentTemplates');
        const templates = templatesRes.data?.data || [];
        const missing = missingIds.map(id => {
            const t = templates.find(tpl => tpl.id === id);
            return { id, name: t?.name || `Consent #${id}`, content: t?.content || '' };
        });

        return { ok: false, missing };
    } catch (error) {
        console.error('Force consents check failed:', error);
        return { ok: true };
    }
};